import React from 'react';
import { ChevronDown } from 'lucide-react';
import { SelectOption } from '../../types/mmmConfig';

interface SelectProps {
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: SelectOption[];
  error?: string;
  helperText?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export const Select: React.FC<SelectProps> = ({
  name,
  label,
  value,
  onChange,
  onBlur,
  options,
  error,
  helperText,
  placeholder,
  required,
  disabled,
  className = '',
}) => {
  return (
    <div className={className}>
      <label htmlFor={name} className="block text-sm font-semibold text-slate-700 mb-2">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      <div className="relative">
        <select
          id={name}
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          className={`
            w-full px-4 py-3 pr-10 text-sm border-2 rounded-xl transition-all duration-200
            appearance-none cursor-pointer
            ${disabled
              ? 'bg-slate-50 text-slate-400 cursor-not-allowed border-slate-200'
              : 'bg-white hover:border-slate-300'}
            ${error
              ? 'border-rose-300 focus:border-rose-500 focus:ring-4 focus:ring-rose-100'
              : 'border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100'}
            focus:outline-none
          `}
        >
          {placeholder && (
            <option value="" className="text-slate-400">
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <ChevronDown className={`w-5 h-5 ${disabled ? 'text-slate-300' : 'text-slate-500'}`} />
        </div>
      </div>
      {error && (
        <p className="text-rose-600 text-xs mt-2 flex items-center gap-1">
          <span className="w-1 h-1 rounded-full bg-rose-500" />
          {error}
        </p>
      )}
      {helperText && !error && (
        <p className="text-slate-500 text-xs mt-2">{helperText}</p>
      )}
    </div>
  );
};
